
import React from "react";
import { format } from "date-fns";
import { pt, enUS, fr, es } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { WeekView } from "./components/StudentWeekView";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";

interface StudentCalendarViewsProps {
  view: 'week';
  date: Date;
  settings: any;
  events: any[];
  handleDateSelect: (date: Date) => void;
  handleEventClick: (event: any) => void;
  setDate: (date: Date) => void;
}

export const StudentCalendarViews = ({
  date,
  settings,
  events,
  handleDateSelect,
  handleEventClick,
  setDate,
}: StudentCalendarViewsProps) => {
  const { language } = useLanguage();
  const { t } = useTranslations("schedule");
  
  // Get the appropriate locale based on language
  const getDateLocale = () => {
    switch (language) {
      case 'pt': return pt;
      case 'es': return es;
      case 'fr': return fr;
      default: return enUS;
    }
  };
  
  const dateLocale = getDateLocale();

  const navigateDate = (direction: 'next' | 'prev') => {
    const newDate = new Date(date);
    if (direction === 'next') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setDate(newDate);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateDate('prev')}
            className="border-slate-200 dark:border-gray-700"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateDate('next')}
            className="border-slate-200 dark:border-gray-700"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold dark:text-white">
            {format(date, "MMMM yyyy", { locale: dateLocale })}
          </h2>
        </div>
      </div>

      <WeekView
        date={date}
        settings={settings}
        events={events}
        handleDateClick={handleDateSelect}
        handleEventClick={handleEventClick}
      />
    </div>
  );
};
